import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';

export interface TimeEntryFilters {
  startDate: Date | null;
  endDate: Date | null;
  projectId: string;
  category: string;
  searchText: string;
  billableOnly: boolean | null;
}

/** Safely convert a value to a Date object (handles strings from localStorage) */
function ensureDate(value: Date | string | null): Date | null {
  if (!value) return null;
  if (value instanceof Date) return value;
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

export function useTimeEntriesFiltered(filters: TimeEntryFilters) {
  // Normalize filters to prevent bugs from serialization
  const startDate = ensureDate(filters.startDate);
  const endDate = ensureDate(filters.endDate);
  const projectId = filters.projectId && filters.projectId !== '__all__' ? filters.projectId : '';
  const category = filters.category && filters.category !== '__all__' ? filters.category : '';
  const searchText = filters.searchText?.trim() || '';
  const billableOnly = filters.billableOnly;

  return useQuery({
    queryKey: ['time_entries_filtered', { startDate: startDate?.toISOString(), endDate: endDate?.toISOString(), projectId, category, searchText, billableOnly }],
    queryFn: async () => {
      let query = supabase
        .from('time_entries')
        .select(`
          *,
          projects(id, name, clients(id, name))
        `)
        .order('start_time', { ascending: false });

      // Date range filter com boundaries limpas (início do dia → início do próximo dia)
      if (startDate) {
        const startDateStr = format(startDate, 'yyyy-MM-dd');
        const startOfDayLocal = new Date(`${startDateStr}T00:00:00`);
        query = query.gte('start_time', startOfDayLocal.toISOString());
      }

      if (endDate) {
        const endDateStr = format(endDate, 'yyyy-MM-dd');
        // Usar início do próximo dia com .lt() garante inclusão completa do último dia
        const nextDayLocal = new Date(`${endDateStr}T00:00:00`);
        nextDayLocal.setDate(nextDayLocal.getDate() + 1);
        query = query.lt('start_time', nextDayLocal.toISOString());
      }

      // Project filter
      if (projectId) {
        query = query.eq('project_id', projectId);
      }

      // Category filter
      if (category) {
        query = query.eq('category', category as "dev" | "meeting" | "research" | "admin");
      }

      // Billable filter
      if (billableOnly !== null && billableOnly !== undefined) {
        query = query.eq('is_billable', billableOnly);
      }

      // Text search server-side com ilike (mais eficiente que filtro client-side)
      if (searchText) {
        query = query.ilike('description', `%${searchText}%`);
      }

      const { data, error } = await query;

      if (error) throw error;

      return data || [];
    },
    enabled: !!(startDate && endDate),
  });
}
